import React, { useEffect, useRef, useState } from "react";
import { autoplayAllowed } from "@/components/home/FeaturedCardMedia";

// Full, uncropped project video with native controls; autoplays muted while on screen.
export const ProjectVideoPlayer = ({ project }) => {
  const videoRef = useRef(null);
  const [auto] = useState(autoplayAllowed);
  const src = project?.homepage_video_cover;
  const m = project?.homepage_video_cover_meta || {};
  const ratio = m.width && m.height ? m.width / m.height : 16 / 9;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const st = { userPaused: false, ours: false };
    const onOther = (e) => { if (e.detail?.id !== v && !v.muted) v.pause(); };
    const announce = () => {
      if (!v.muted && !v.paused) window.dispatchEvent(new CustomEvent("ur:media:play", { detail: { id: v } }));
    };
    const onPause = () => { if (!st.ours) st.userPaused = true; st.ours = false; };
    const onPlay = () => { st.userPaused = false; announce(); };
    window.addEventListener("ur:media:play", onOther);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("volumechange", announce);
    let io;
    if (auto) v.muted = true;
    if (auto && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) {
          if (!st.userPaused && v.paused) {
            const p = v.play();
            if (p?.catch) p.catch(() => {});
          }
        } else if (!v.paused) {
          st.ours = true;
          v.pause();
        }
      }, { threshold: 0.25 });
      io.observe(v);
    }
    return () => {
      io?.disconnect();
      window.removeEventListener("ur:media:play", onOther);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("volumechange", announce);
      v.pause();
    };
  }, [src, auto]);

  if (!src) return null;
  const type = /\.webm$/i.test(src) ? "video/webm" : "video/mp4";

  return (
    <section data-testid="detail-video" className="bg-[#0F0F0F]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14">
        <div
          className="mx-auto rounded-2xl overflow-hidden border border-[#262626] bg-black"
          style={{ aspectRatio: String(ratio), width: "100%", maxWidth: `min(100%, calc(80vh * ${ratio}))` }}
        >
          <video
            ref={videoRef}
            key={src}
            controls
            playsInline
            muted={auto}
            loop={auto}
            preload={auto ? "auto" : "metadata"}
            aria-label={`${project.title} — project video`}
            data-testid="detail-video-player"
            className="block w-full h-full object-contain"
          >
            {/* #t fragment makes Safari paint the first frame instead of a black box */}
            <source src={`${src}#t=0.1`} type={type} />
            Your browser can’t play this video. <a href={src}>Download the video</a>.
          </video>
        </div>
      </div>
    </section>
  );
};
